import type {
  AiAccessPromptView,
  AiFeatureAction,
} from "@/features/ai/ai.types";
import type { SubscriptionTier } from "@/types/projects";

export type AiActionErrorCode =
  | "access-denied"
  | "budget-restricted"
  | "daily-limit-reached"
  | "failed"
  | "insufficient-credits"
  | "invalid-request"
  | "not-found"
  | "rate-limit-reached"
  | "selection-too-short"
  | "service-busy"
  | "tier-locked";

export type AiActionErrorCacheStatus =
  | "hit"
  | "invalidated"
  | "miss"
  | "none"
  | "not-applicable";

export type AiActionErrorBody = {
  cacheStatus?: AiActionErrorCacheStatus | null;
  code?: string;
  creditsSafe?: boolean | null;
  currentBalance?: number | null;
  message?: string;
  recommendedTier?: SubscriptionTier | null;
  resetAt?: string | null;
  retryAfterSeconds?: number | null;
  stage?: string | null;
  truncated?: boolean | null;
  usesRemainingToday?: number | null;
};

export interface AiActionErrorDetails {
  cacheStatus?: AiActionErrorCacheStatus | null;
  creditsSafe?: boolean;
  resetAt?: string | null;
  retryAfterSeconds?: number | null;
  stage?: string | null;
  truncated?: boolean;
}

function formatPromptDate(resetAt: string | null) {
  if (!resetAt) {
    return "";
  }

  const date = new Date(resetAt);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

function getPromptMinutes(error: AiActionError) {
  if (typeof error.retryAfterSeconds === "number" && Number.isFinite(error.retryAfterSeconds)) {
    return Math.max(1, Math.ceil(error.retryAfterSeconds / 60));
  }

  if (!error.resetAt) {
    return 1;
  }

  const diffMs = new Date(error.resetAt).getTime() - Date.now();
  return Number.isFinite(diffMs) ? Math.max(1, Math.ceil(diffMs / 60000)) : 1;
}

export class AiActionError extends Error {
  public readonly cacheStatus: AiActionErrorCacheStatus | null;
  public readonly creditsSafe: boolean;
  public readonly resetAt: string | null;
  public readonly retryAfterSeconds: number | null;
  public readonly stage: string | null;
  public readonly truncated: boolean;

  constructor(
    public readonly code: AiActionErrorCode,
    message: string,
    public readonly currentBalance: number | null = null,
    public readonly recommendedTier: SubscriptionTier | null = null,
    public readonly usesRemainingToday: number | null = null,
    details: AiActionErrorDetails = {},
  ) {
    super(message);
    this.name = "AiActionError";
    this.cacheStatus = details.cacheStatus ?? null;
    this.creditsSafe = details.creditsSafe ?? false;
    this.resetAt = details.resetAt ?? null;
    this.retryAfterSeconds = details.retryAfterSeconds ?? null;
    this.stage = details.stage ?? null;
    this.truncated = details.truncated ?? false;
  }
}

function coerceCacheStatus(
  value: AiActionErrorBody["cacheStatus"],
): AiActionErrorCacheStatus | null {
  if (value === "hit" || value === "miss" || value === "not-applicable") {
    return value;
  }

  if (value === "invalidated") {
    return "miss";
  }

  if (value === "none") {
    return "not-applicable";
  }

  return null;
}

function mapAiActionErrorDetails(
  body: AiActionErrorBody | null | undefined,
): AiActionErrorDetails {
  return {
    cacheStatus: coerceCacheStatus(body?.cacheStatus),
    creditsSafe: body?.creditsSafe === true,
    resetAt:
      typeof body?.resetAt === "string" && body.resetAt.trim().length > 0
        ? body.resetAt
        : null,
    retryAfterSeconds:
      typeof body?.retryAfterSeconds === "number" && Number.isFinite(body.retryAfterSeconds)
        ? body.retryAfterSeconds
        : null,
    stage:
      typeof body?.stage === "string" && body.stage.trim().length > 0
        ? body.stage
        : null,
    truncated: body?.truncated === true,
  };
}

export function mapAiActionErrorBody(
  body: AiActionErrorBody | null | undefined,
  fallbackMessage = "AI_ACTION_FAILED",
) {
  const code = body?.code ?? fallbackMessage;
  const details = mapAiActionErrorDetails(body);

  switch (code) {
    case "TIER_LOCKED":
      return new AiActionError(
        "tier-locked",
        body?.message ?? code,
        body?.currentBalance ?? null,
        body?.recommendedTier ?? "crafter",
        null,
        details,
      );
    case "INSUFFICIENT_CREDITS":
      return new AiActionError(
        "insufficient-credits",
        body?.message ?? code,
        body?.currentBalance ?? 0,
        null,
        null,
        details,
      );
    case "DAILY_LIMIT_REACHED":
      return new AiActionError(
        "daily-limit-reached",
        body?.message ?? code,
        null,
        null,
        body?.usesRemainingToday ?? 0,
        details,
      );
    case "AI_RATE_LIMIT_REACHED":
      return new AiActionError(
        "rate-limit-reached",
        body?.message ?? code,
        body?.currentBalance ?? null,
        body?.recommendedTier ?? null,
        body?.usesRemainingToday ?? null,
        details,
      );
    case "AI_BUDGET_RESTRICTED":
    case "AI_IMAGE_BUDGET_RESTRICTED":
      return new AiActionError(
        "budget-restricted",
        body?.message ?? code,
        body?.currentBalance ?? null,
        body?.recommendedTier ?? null,
        body?.usesRemainingToday ?? null,
        details,
      );
    case "AI_SERVICE_BUSY":
      return new AiActionError(
        "service-busy",
        body?.message ?? code,
        body?.currentBalance ?? null,
        body?.recommendedTier ?? null,
        body?.usesRemainingToday ?? null,
        details,
      );
    case "PROJECT_ACCESS_DENIED":
      return new AiActionError(
        "access-denied",
        body?.message ?? code,
        null,
        null,
        null,
        details,
      );
    case "CHAPTER_RANGE_INVALID":
    case "POLISH_CHAPTER_TOO_SHORT":
      return new AiActionError(
        "invalid-request",
        body?.message ?? code,
        null,
        null,
        null,
        details,
      );
    case "ENHANCE_SELECTION_TOO_SHORT":
      return new AiActionError(
        "selection-too-short",
        body?.message ?? code,
        null,
        null,
        null,
        details,
      );
    case "STORY_NOT_FOUND":
    case "STRUCTURAL_ANALYSIS_NOT_FOUND":
    case "STRUCTURAL_ANALYSIS_FINDING_NOT_FOUND":
    case "CHAPTER_NOT_FOUND":
      return new AiActionError(
        "not-found",
        body?.message ?? code,
        null,
        null,
        null,
        details,
      );
    default:
      return new AiActionError(
        "failed",
        body?.message ?? code,
        body?.currentBalance ?? null,
        body?.recommendedTier ?? null,
        body?.usesRemainingToday ?? null,
        details,
      );
  }
}

export function getAiActionPrompt(error: AiActionError): AiAccessPromptView | null {
  switch (error.code) {
    case "tier-locked":
      return {
        ctaLabelKey: "story.ai.access.actions.upgrade",
        descriptionKey: "story.ai.access.upgradePrompt",
        kind: "upgrade",
        recommendedTier: error.recommendedTier,
      };
    case "insufficient-credits":
      return {
        ctaLabelKey: "story.ai.access.actions.topUp",
        descriptionKey: "story.ai.access.topUpPrompt",
        kind: "top-up",
      };
    case "daily-limit-reached":
      return {
        ctaLabelKey: null,
        descriptionKey: "story.ai.access.dailyLimitReached",
        kind: "daily-limit",
      };
    case "rate-limit-reached":
      return {
        ctaLabelKey: null,
        descriptionKey: "story.ai.access.rateLimitPrompt",
        descriptionValues: {
          minutes: getPromptMinutes(error),
        },
        kind: "wait",
      };
    case "budget-restricted":
      return {
        ctaLabelKey: null,
        descriptionKey: "story.ai.access.budgetRestrictedPrompt",
        descriptionValues: {
          resumeDate: formatPromptDate(error.resetAt),
        },
        kind: "budget-restricted",
      };
    case "service-busy":
      return {
        ctaLabelKey: null,
        descriptionKey: "story.ai.access.serviceBusyPrompt",
        kind: "wait",
      };
    default:
      return null;
  }
}

export function getAiActionErrorMessageKey(
  feature: AiFeatureAction,
  error: AiActionError,
) {
  const baseKey =
    feature === "continuation"
      ? "story.ai.continuation.errors"
      : feature === "continuity"
        ? "story.ai.continuity.errors"
        : feature === "structural"
          ? "story.ai.structural.errors"
          : feature === "enhance"
            ? "story.ai.enhance.errors"
            : feature === "polish"
              ? "story.ai.polish.errors"
          : "story.ai.chat.errors";

  switch (error.code) {
    case "tier-locked":
      return `${baseKey}.tierLocked`;
    case "insufficient-credits":
      return `${baseKey}.insufficientCredits`;
    case "daily-limit-reached":
      return `${baseKey}.dailyLimitReached`;
    case "rate-limit-reached":
      return `${baseKey}.rateLimitReached`;
    case "budget-restricted":
      return `${baseKey}.budgetRestricted`;
    case "service-busy":
      return `${baseKey}.serviceBusy`;
    case "access-denied":
      return `${baseKey}.accessDenied`;
    case "invalid-request":
      return `${baseKey}.invalidRequest`;
    case "selection-too-short":
      return `${baseKey}.tooShort`;
    case "not-found":
      return `${baseKey}.notFound`;
    default:
      return `${baseKey}.failed`;
  }
}

export function formatContinuationSuggestionErrorMessage(error: AiActionError) {
  return getAiActionErrorMessageKey("continuation", error);
}
